import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop';
import GlobalRouteSeo from './components/seo/GlobalRouteSeo';
import CookieConsent from './components/cookie-consent/CookieConsent';
import GlobalAnalyticsObserver from './components/analytics/GlobalAnalyticsObserver';
import { AuthProvider } from './context/AuthContext';
import './App.css';

import LandingPage from './pages/Landing/Landing';

const OurMission           = lazy(() => import('./pages/OurMission/OurMission'));
const AboutPage            = lazy(() => import('./pages/About/About'));
const CaseStudy            = lazy(() => import('./pages/CaseStudy/CaseStudy'));
const ContactUs            = lazy(() => import('./pages/ContactUs/ContactUs'));
const Legal                = lazy(() => import('./pages/Legal/Legal'));
const Privacy              = lazy(() => import('./pages/Privacy/Privacy'));
const TermsOfService       = lazy(() => import('./pages/TermsOfService/TermsOfService'));
const CookiePolicy         = lazy(() => import('./pages/CookiePolicy/CookiePolicy'));
const Guides               = lazy(() => import('./pages/Guides/Guides'));
const BlogList             = lazy(() => import('./pages/Blog/BlogList/BlogList'));
const BlogPost             = lazy(() => import('./pages/Blog/BlogPost/BlogPost'));
const BlogPostDataPrivacy  = lazy(() => import('./pages/Blog/BlogPostDataPrivacy/BlogPostDataPrivacy'));
const BlogPostDiversity    = lazy(() => import('./pages/Blog/BlogPostDiversity/BlogPostDiversity'));
const BlogPostIndiaReferrals = lazy(() => import('./pages/Blog/BlogPostIndiaReferrals/BlogPostIndiaReferrals'));
const BlogPostIndiaSkills  = lazy(() => import('./pages/Blog/BlogPostIndiaSkills/BlogPostIndiaSkills'));
const BlogPostSideIncome   = lazy(() => import('./pages/Blog/BlogPostSideIncome/BlogPostSideIncome'));
const BlogPostReferralSoftwareIndia = lazy(() => import('./pages/Blog/BlogPostReferralSoftwareIndia/BlogPostReferralSoftwareIndia'));
const BlogPostRecruitmentAutomation = lazy(() => import('./pages/Blog/BlogPostRecruitmentAutomation/BlogPostRecruitmentAutomation'));
const BlogPostSkillsBasedHiring = lazy(() => import('./pages/Blog/BlogPostSkillsBasedHiring/BlogPostSkillsBasedHiring'));
const DynamicBlogPost           = lazy(() => import('./pages/Blog/DynamicBlogPost/DynamicBlogPost'));
const FAQPage              = lazy(() => import('./pages/FAQ/FAQ'));
const CareerPage           = lazy(() => import('./pages/Career/Career'));
const ServicesPage         = lazy(() => import('./pages/Services/Services'));
const ForJobSeekers        = lazy(() => import('./pages/ForJobSeekers/ForJobSeekers'));
const ForProfessionals     = lazy(() => import('./pages/ForProfessionals/ForProfessionals'));
const ResumeReview         = lazy(() => import('./pages/ResumeReview/ResumeReview'));
const MockInterview        = lazy(() => import('./pages/MockInterview/MockInterview'));
const ReferSession         = lazy(() => import('./pages/ReferSession/ReferSession'));
const WeekReferDrop        = lazy(() => import('./pages/WeekReferDrop/WeekReferDrop'));

/**
 * All auth + onboarding flows now live in the dashboard app (`my-app`).
 * Any hit to /login, /signup, /onboarding, etc. is redirected to that app.
 */
const DASHBOARD_URL = (process.env.REACT_APP_DASHBOARD_URL || 'http://localhost:3002').replace(/\/$/, '');

function RedirectToApp({ path }: { path: string }) {
  const search = window.location.search || '';
  const hash = window.location.hash || '';
  window.location.replace(`${DASHBOARD_URL}${path}${search}${hash}`);
  return null;
}

function PageLoader() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#000000',
      }}
      aria-label="Loading page"
      role="status"
    >
      <div
        style={{
          width: 40,
          height: 40,
          border: '3px solid rgba(255,255,255,0.08)',
          borderTopColor: '#f59e0b',
          borderRadius: '50%',
          animation: 'spin 0.7s linear infinite',
        }}
      />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <GlobalRouteSeo />
        <ScrollToTop />
        <CookieConsent />
        <GlobalAnalyticsObserver />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Landing / Marketing */}
            <Route path="/"       element={<LandingPage />} />
            <Route path="/landing" element={<LandingPage />} />

            {/* Auth + onboarding — moved to dashboard app */}
            <Route path="/login"                element={<RedirectToApp path="/login" />} />
            <Route path="/signup"               element={<RedirectToApp path="/signup" />} />
            <Route path="/forgot-password"      element={<RedirectToApp path="/forgot-password" />} />
            <Route path="/verify-email"         element={<RedirectToApp path="/verify-email" />} />
            <Route path="/oauth/google/success" element={<RedirectToApp path="/oauth/google/success" />} />
            <Route path="/onboarding"           element={<RedirectToApp path="/onboarding" />} />
            <Route path="/pending-verification" element={<RedirectToApp path="/pending-verification" />} />

            {/* Info pages */}
            <Route path="/our-mission"    element={<OurMission />} />
            <Route path="/services"       element={<ServicesPage />} />
            <Route path="/for-job-seekers" element={<ForJobSeekers />} />
            <Route path="/for-professionals" element={<ForProfessionals />} />
            <Route path="/resume-review" element={<ResumeReview />} />
            <Route path="/mock-interview" element={<MockInterview />} />
            <Route path="/refer-session" element={<ReferSession />} />
            <Route path="/week-refer-drop" element={<WeekReferDrop />} />
            <Route path="/about"          element={<AboutPage />} />
            <Route path="/case-study"     element={<CaseStudy />} />
            <Route path="/contact-us"     element={<ContactUs />} />
            <Route path="/legal"          element={<Legal />} />
            <Route path="/privacy"        element={<Privacy />} />
            <Route path="/terms"          element={<TermsOfService />} />
            <Route path="/cookie-policy"  element={<CookiePolicy />} />
            <Route path="/guides"         element={<Guides />} />
            <Route path="/faq"            element={<FAQPage />} />
            <Route path="/career"         element={<CareerPage />} />

            {/* Blog */}
            <Route path="/blog"                                                      element={<BlogList />} />
            <Route path="/blog/the-power-of-networking-and-referrals"               element={<BlogPost />} />
            <Route path="/blog/navigating-data-privacy-in-modern-recruitment"       element={<BlogPostDataPrivacy />} />
            <Route path="/blog/building-compliant-and-diverse-referral-programs"    element={<BlogPostDiversity />} />
            <Route path="/blog/why-employee-referrals-drive-it-hires-india"         element={<BlogPostIndiaReferrals />} />
            <Route path="/blog/skills-first-hiring-india-tech-market"               element={<BlogPostIndiaSkills />} />
            <Route path="/blog/side-income-passive-income-software-engineers-india" element={<BlogPostSideIncome />} />
            <Route path="/blog/employee-referral-software-india-buyers-guide"       element={<BlogPostReferralSoftwareIndia />} />
            <Route path="/blog/recruitment-automation-software-reduce-time-to-hire" element={<BlogPostRecruitmentAutomation />} />
            <Route path="/blog/skills-based-hiring-platform-india-tech"             element={<BlogPostSkillsBasedHiring />} />
            {/* Dynamic route: catches any new slug published from the admin CMS */}
            <Route path="/blog/:slug"                                               element={<DynamicBlogPost />} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </Router>
    </AuthProvider>
  );
}

export default App;
